import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
// Ag-Grid
import {
  GridOptions,
  GridApi,
  ColDef,
  RowSelectionOptions,
} from 'ag-grid-community';
import { agGridDarkTheme } from 'src/app/shared/components/ag-grid/dark-theme';
import { AgGridAngular } from 'ag-grid-angular';
import { RowspanCellComponent } from '../shared/components/ag-grid/rowspan-cell.components';
// Services
import { SharedService } from '../shared/services/shared.service';
// Third Party
import { Subscription, firstValueFrom } from 'rxjs';
import { ReservedFirearmsTableColumnDef } from './columnDefs';
import { RevertBtnComponent } from '../shared/components/ag-grid/revert-btn/revert-btn.component';
import {
  Answers,
  ModalID,
  Questions,
  State,
  StatusModalIndicator,
} from '../shared/models/constants';
import { ReservedFirearm } from '../shared/models/general';
import { AccountingService } from '../accounting/services/accounting.service';

@Component({
  selector: 'app-reserved-firearms',
  templateUrl: './reserved-firearms.component.html',
  styleUrl: './reserved-firearms.component.scss',
  standalone: true,
  imports: [AgGridAngular],
})
export class ReservedFirearmsComponent {
  subscriptions = new Subscription();
  private destroyRef = inject(DestroyRef);
  public rowSelection: RowSelectionOptions = {
    mode: 'multiRow',
    enableSelectionWithoutKeys: true,
    checkboxes: false,
  };
  // ag-grid properties Grid
  gridApi!: GridApi;

  columnDefs: ColDef[] = ReservedFirearmsTableColumnDef(this.accountingService);
  defaultColDef;
  //sideBar;
  // detailCellRendererParams;
  // statusBar;
  detailCellRendererParams: any;
  overlayLoadingTemplate;
  overlayNoRowsTemplate;
  gridOptions: GridOptions = {
    theme: agGridDarkTheme,};
  components: any;
  theme = agGridDarkTheme;

  constructor(
    private router: Router,
    public sharedService: SharedService,
    public accountingService: AccountingService,
  ) {
    this.gridOptions = <GridOptions>{};
    this.gridOptions.headerHeight = 50;
    this.gridOptions.rowHeight = 28;
    this.gridOptions.context = { ConfigComponent: this }; // this allows access to component functions
    this.gridOptions.rowClass = 'ag-grid-row';
    this.gridOptions.suppressRowTransform = true;
    this.defaultColDef = {
      sortable: true,
      filter: true,
      resizable: true,
      floatingFilter: true,
      wrapHeaderText: true,
    };
    this.rowSelection = {
      mode: 'singleRow',
      enableSelectionWithoutKeys: true,
      checkboxes: false,
    };
    this.components = {
      RevertBtn: RevertBtnComponent,
      rowspanCellRenderer: RowspanCellComponent, // need to understand why the rows are not merging
    };

    // yes / no submit
    this.subscriptions.add(
      this.sharedService.yes_no_submit$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((response) => {
          if (
            response['answer'] === Answers.yes &&
            response['question'] === Questions.revert_firearm
          ) {
            this.removeFirearmFromList(
              response['optional_details']['colValues'],
            );
          } else if (
            response['answer'] === Answers.yes &&
            response['question'] === Questions.revert_firearm_all
          ) {
            this.removeAllFirearms();
          }
        }),
    );
  }

  onGridReady(params: any) {
    this.gridApi = params.api;
  }
  async ngOnInit() {
    this.addButtonColumn();
    await this.getReservedFirearmsRecords();
    if (this.sharedService.bound_book.length === 0) {
      await this.sharedService.getBoundBookEntries();
    }
  }

  addButtonColumn() {
    const hasEditDelete = this.columnDefs.some(
      (column) => column.headerName === 'Revert / Select',
    );
    if (!hasEditDelete) {
      this.columnDefs.push({
        headerName: 'Remove / Select',
        field: 'revert',
        resizable: true,
        width: 200,
        pinned: 'right',
        cellRenderer: 'RevertBtn',
        cellRendererParams: {
          revertItemClick: this.revertFirearmClick.bind(this),
          selectItemClick: this.selectFirearmClick.bind(this),
        },
      });
    }
  }
  async getReservedFirearmsRecords() {
    this.sharedService.openStatusSpinner('Retrieving Reserved Firearms');
    await this.sharedService.getReservedFirearmList();
    this.sharedService.reserved_firearm_list = [
      ...this.sharedService.reserved_firearm_list,
    ];
    this.sharedService.closeStatusSpinner();
  }
  revertFirearmClick(data) {
    this.sharedService.openYesNoModal(Questions.revert_firearm, data);
  }
  selectFirearmClick(data) {
    this.sharedService.selected_reservation_id =
      data['colValues'].reservation_id;
    if (!this.sharedService.dispo_build_modal_shown) {
      this.sharedService.changeRoute('disposition');
      this.sharedService.reserved_firearm_selected$.next(
        this.sharedService.selected_reservation_id,
      );
    } else {
      this.sharedService.closeModal(ModalID.dispo_build);
      this.sharedService.dispo_build_reserved_firearms = [];
      for (const firearm of this.sharedService.reserved_firearm_list) {
        if (firearm.reservation_id === data['colValues'].reservation_id) {
          this.sharedService.dispo_build_reserved_firearms.push(firearm);
          this.sharedService.build_disposition.reserved_firearms = firearm;
        }
      }
      this.sharedService.reserved_firearm_selected$.next(
        this.sharedService.selected_reservation_id,
      );
    }
  }
  async removeFirearmFromList(reserved_firearm: ReservedFirearm) {
    this.sharedService.openStatusSpinner('Removing Firearm from Reserve List.');
    try {
      await this.sharedService.deleteFirearmFromReserveList(
        reserved_firearm.reservation_id,
      );
    } catch (error) {
      console.error('removeFirearmFromList - deleteFirearmFromReserveList error:', error);
      this.sharedService.raiseErrorToast('Failed to remove firearm from reserve list.');
      this.sharedService.closeStatusSpinner();
      return;
    }
    try {
      const bound_book_entry =
        this.sharedService.bound_book.find(
          (entry) => entry.firearm_id == reserved_firearm.firearm_id,
        ) ||
        (await this.sharedService.getBoundBookEntryByFirearmId(
          reserved_firearm.firearm_id,
        ));
      if (bound_book_entry) {
        bound_book_entry.state = State.acquisition;
        await this.sharedService.updateBoundBookEntry(bound_book_entry);
      }
    } catch (error) {
      console.error('removeFirearmFromList - updateBoundBookEntry error:', error);
    } finally {
      this.sharedService.closeStatusSpinner();
    }
  }
  reserveFirearm() {
    this.router.navigate(['disposition-selection']);
  }
  closeComponent() {
    if (this.sharedService.dispo_build_modal_shown) {
      this.sharedService.closeModal(ModalID.dispo_build);
      setTimeout(() => {
        this.sharedService.dispo_build_modal_shown = false;
      }, 200);
    } else {
      this.sharedService.changeRoute('home');
    }
  }
  async removeAllFirearms() {
    const firearms = [...this.sharedService.reserved_firearm_list];
    for (const firearm of firearms) {
      await this.removeFirearmFromList(firearm);
    }
    this.sharedService.raiseSuccessToast('All firearms removed from reserve list.');
  }
  async removeAllFirearmsClick() {
    this.sharedService.openYesNoModal(Questions.revert_firearm_all);
  }
  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
